import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import * as Cesium from 'cesium';

export interface ExtentPolygon {
  type: 'Polygon';
  coordinates: number[][][];
}

interface UseQueryExtentDrawArgs {
  viewerRef: MutableRefObject<Cesium.Viewer | null>;
  /** Fired when a valid polygon (>= 3 vertices) is closed. */
  onComplete: (polygon: ExtentPolygon) => void;
  /** Fired when the user aborts drawing (right-click) before closing. */
  onCancel: () => void;
}

// A dedicated data source keeps the search-extent polygon separate from
// `viewer.entities`, so highlighting query results (which calls
// `entities.removeAll()`) never wipes the active extent (US-103163 Scenario 8).
const EXTENT_DATA_SOURCE = 'query-search-extent';
// Pixel distance under which a click is treated as "on the starting vertex".
const CLOSE_PIXEL_THRESHOLD = 14;
const DRAW_COLOR = '#ff6600';
const EXTENT_COLOR = '#076169';
const CUSTOM_CROSSHAIR = `url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='16' cy='16' r='10' stroke='white' stroke-width='2'/%3E%3Cpath d='M16 2V8M16 24V30M2 16H8M24 16H30' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3Ccircle cx='16' cy='16' r='1.5' fill='white'/%3E%3C/svg%3E") 16 16, crosshair`;

/**
 * Polygon-drawing interaction for the Query Tool's search extent.
 * Vertices are added on left-click, a rubber-band edge tracks the cursor, and
 * clicking the first vertex again (with >= 3 vertices) closes the polygon.
 */
export const useQueryExtentDraw = ({ viewerRef, onComplete, onCancel }: UseQueryExtentDrawArgs) => {
  const [isDrawing, setIsDrawing] = useState(false);

  const handlerRef = useRef<Cesium.ScreenSpaceEventHandler | null>(null);
  const pointsRef = useRef<Cesium.Cartesian3[]>([]);
  const mouseRef = useRef<Cesium.Cartesian3 | null>(null);
  const vertexEntitiesRef = useRef<Cesium.Entity[]>([]);
  const previewEntityRef = useRef<Cesium.Entity | null>(null);
  const closingLineRef = useRef<Cesium.Entity | null>(null);
  // Refs keep the handler (attached once) from closing over stale callbacks.
  const onCompleteRef = useRef(onComplete);
  const onCancelRef = useRef(onCancel);
  onCompleteRef.current = onComplete;
  onCancelRef.current = onCancel;

  const getExtentDataSource = useCallback((viewer: Cesium.Viewer): Cesium.CustomDataSource => {
    const existing = viewer.dataSources.getByName(EXTENT_DATA_SOURCE)[0];
    if (existing) return existing as Cesium.CustomDataSource;
    const ds = new Cesium.CustomDataSource(EXTENT_DATA_SOURCE);
    viewer.dataSources.add(ds);
    return ds;
  }, []);

  const cartesianToLngLat = (position: Cesium.Cartesian3): [number, number] => {
    const carto = Cesium.Cartographic.fromCartesian(position);
    return [Cesium.Math.toDegrees(carto.longitude), Cesium.Math.toDegrees(carto.latitude)];
  };

  const resetCursor = (viewer: Cesium.Viewer) => {
    viewer.scene.canvas.style.cursor = '';
  };

  /** Tear down the in-progress sketch (vertex markers + rubber-band preview). */
  const clearSketch = useCallback((viewer: Cesium.Viewer) => {
    vertexEntitiesRef.current.forEach((e) => viewer.entities.remove(e));
    vertexEntitiesRef.current = [];
    if (previewEntityRef.current) {
      viewer.entities.remove(previewEntityRef.current);
      previewEntityRef.current = null;
    }
    if (closingLineRef.current) {
      viewer.entities.remove(closingLineRef.current);
      closingLineRef.current = null;
    }
    pointsRef.current = [];
    mouseRef.current = null;
  }, []);

  /** Remove the committed search-extent polygon from the map. */
  const clearExtent = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    getExtentDataSource(viewer).entities.removeAll();
  }, [viewerRef, getExtentDataSource]);

  const stopDrawing = useCallback(
    (viewer: Cesium.Viewer) => {
      handlerRef.current?.destroy();
      handlerRef.current = null;
      clearSketch(viewer);
      resetCursor(viewer);
      setIsDrawing(false);
    },
    [clearSketch],
  );

  /** Stop an in-progress sketch without committing a polygon (e.g. user
   *  switched back to the All extent). Safe to call when not drawing. */
  const cancelDraw = useCallback(() => {
    const viewer = viewerRef.current;
    if (viewer && !viewer.isDestroyed()) stopDrawing(viewer);
  }, [viewerRef, stopDrawing]);

  const beginDraw = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    getExtentDataSource(viewer).entities.removeAll();
    handlerRef.current?.destroy();
    clearSketch(viewer);

    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    handlerRef.current = handler;
    viewer.scene.canvas.style.cursor = CUSTOM_CROSSHAIR;

    // Disable double click
    viewer.screenSpaceEventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

    setIsDrawing(true);

    const pickPosition = (windowPosition: Cesium.Cartesian2) => viewer.camera.pickEllipsoid(windowPosition, viewer.scene.globe.ellipsoid);

    handler.setInputAction((click: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
      const cartesian = pickPosition(click.position);
      if (!cartesian) return;

      if (pointsRef.current.length >= 3) {
        const firstWindow = Cesium.SceneTransforms.wgs84ToWindowCoordinates(viewer.scene, pointsRef.current[0]);
        if (firstWindow && Cesium.Cartesian2.distance(firstWindow, click.position) < CLOSE_PIXEL_THRESHOLD) {
          const ring = pointsRef.current.map(cartesianToLngLat);
          ring.push(ring[0]);
          const polygon: ExtentPolygon = { type: 'Polygon', coordinates: [ring] };

          // Final Polygon Style
          getExtentDataSource(viewer).entities.add({
            polygon: {
              hierarchy: new Cesium.PolygonHierarchy([...pointsRef.current]),
              material: Cesium.Color.fromCssColorString(EXTENT_COLOR).withAlpha(0.6),
              outline: true,
              outlineColor: Cesium.Color.fromCssColorString(EXTENT_COLOR),
              outlineWidth: 2,
            },
          });

          stopDrawing(viewer);
          onCompleteRef.current(polygon);
          return;
        }
      }

      pointsRef.current.push(cartesian);
      const vertex = viewer.entities.add({
        position: cartesian,
        point: {
          pixelSize: 11, // Match Measure Tool
          color: Cesium.Color.fromCssColorString(DRAW_COLOR),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
      });
      vertexEntitiesRef.current.push(vertex);

      // Create Preview (Polygon Fill + Polyline Edge)
      if (!previewEntityRef.current) {
        previewEntityRef.current = viewer.entities.add({
          polygon: {
            hierarchy: new Cesium.CallbackProperty(() => {
              const pts = [...pointsRef.current];
              if (mouseRef.current) pts.push(mouseRef.current);
              return new Cesium.PolygonHierarchy(pts);
            }, false),
            material: Cesium.Color.fromCssColorString(DRAW_COLOR).withAlpha(0.3),
          },
          polyline: {
            positions: new Cesium.CallbackProperty(() => {
              const pts = [...pointsRef.current];
              if (mouseRef.current) pts.push(mouseRef.current);
              return pts;
            }, false),
            width: 2,
            material: Cesium.Color.fromCssColorString(DRAW_COLOR),
          },
        });
      }

      // Create Dotted Closing Line
      if (!closingLineRef.current) {
        closingLineRef.current = viewer.entities.add({
          polyline: {
            positions: new Cesium.CallbackProperty(() => {
              if (pointsRef.current.length > 0 && mouseRef.current) {
                return [pointsRef.current[0], mouseRef.current];
              }
              return [];
            }, false),
            width: 2,
            material: new Cesium.PolylineDashMaterialProperty({
              color: Cesium.Color.fromCssColorString(DRAW_COLOR),
              dashLength: 8.0,
            }),
          },
        });
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    handler.setInputAction((movement: Cesium.ScreenSpaceEventHandler.MotionEvent) => {
      mouseRef.current = pickPosition(movement.endPosition) ?? null;
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    handler.setInputAction(() => {
      stopDrawing(viewer);
      onCancelRef.current();
    }, Cesium.ScreenSpaceEventType.RIGHT_CLICK);
  }, [viewerRef, getExtentDataSource, clearSketch, stopDrawing]);

  // Safety net: destroy the handler / detach the data source on unmount.
  useEffect(() => {
    return () => {
      const viewer = viewerRef.current;
      handlerRef.current?.destroy();
      handlerRef.current = null;
      if (viewer && !viewer.isDestroyed()) {
        clearSketch(viewer);
        resetCursor(viewer);
        const ds = viewer.dataSources.getByName(EXTENT_DATA_SOURCE)[0];
        if (ds) viewer.dataSources.remove(ds, true);
      }
    };
  }, [viewerRef, clearSketch]);

  return { isDrawing, beginDraw, cancelDraw, clearExtent };
};
